import { Entry } from "contentful";
import type { BaseHit, Hit } from "instantsearch.js";
import Head from "next/head";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { useInstantSearch } from "react-instantsearch";

import { useConfiguration } from "~/contexts/configuration";
import { usePageTracked, usePageTrackingControls } from "~/contexts/page-trackings";
import Algolia from "~/types/algolia";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import TrackingUtils from "~/utils/tracking-utils";

import Container from "../common/container";
import EditorialEntry from "../editorials/editorial-entry";
import CustomInfiniteHits from "../search/custom-infinite-hits";
import CustomSearchBox from "../search/custom-search-box";
import NoResults from "../search/no-results";
import CategoryFilters from "./category-filters";
import Facets from "./facets";
import ProductTileAlgolia from "./product-tile-algolia";
import styles from "./search-product-grid.module.scss";

export function Hit({ hit, sendEvent, index }: { hit: any; sendEvent?: any; index?: number }) {
  return (
    <ProductTileAlgolia
      product={hit}
      listScope="search"
      sendEvent={sendEvent}
      noLazy={index !== undefined && index < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_SEARCH}
    />
  );
}

type Props = {
  noResultsComponents?: Entry<unknown>[];
};

export default function SearchProductGridAlgolia(props: Props) {
  const t = useTranslations();
  const { results } = useInstantSearch();
  const { configuration, algoliaIndexId } = useConfiguration();
  const searchParams = useSearchParams();

  const hits = useRef<Algolia.Product[]>([]);
  const lastTrackedIndex = useRef<number>(0);
  const isPageTracked = usePageTracked();
  const trackingControls = usePageTrackingControls();

  const searchKey = `${algoliaIndexId}[query]`;
  const actualQuery = searchParams.get(searchKey);

  useEffect(() => {
    if (isPageTracked) {
      if (hits.current) {
        _trackImpressions(false);
      }
    } else {
      lastTrackedIndex.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageTracked, hits.current]);

  function _trackImpressions(reset: boolean) {
    lastTrackedIndex.current = TrackingUtils.trackAlgoliaProductsListImpression(
      hits.current,
      reset ? 0 : lastTrackedIndex.current,
      "search",
      {
        currency: configuration.CurrencyID,
      }
    );
  }

  return (
    <>
      <Head>
        <title>{`${t("generic.searched")} "${actualQuery || ""}" | ${t("generic.pageTitleSuffix")}`}</title>
        <meta name="robots" content="none" />
      </Head>
      <div className={styles.searchedBar}>
        <p>{t("generic.searched")}</p>
        <CustomSearchBox
          onChange={() => {
            trackingControls.forceStatus(false);
          }}
        />
      </div>
      <Container className={styles.reducedMobileContainer}>
        {results.nbHits > 0 ? (
          <div className={styles.prodCount}>
            <p>{t("generic.n_products", { number: results.nbHits })}</p>
          </div>
        ) : (
          <div className={styles.noResults}>
            <h2>{t("generic.noresults")}</h2>
            <p dangerouslySetInnerHTML={{ __html: t("generic.noresultsparagraph") }} />
          </div>
        )}
        <NoResults />
        <Facets />
        <CustomInfiniteHits
          hitComponent={Hit}
          showPrevious={false}
          hitsCallback={(newHits: Hit<BaseHit>[]) => {
            hits.current = newHits as Algolia.Product[];
          }}
        />
        {results.nbHits > 0 && (
          <div className={styles.filters}>
            <CategoryFilters pageType="inSearch" />
          </div>
        )}
      </Container>
      {/* show editorial entries from search page contentful */}
      {!results.nbHits &&
        props.noResultsComponents
          ?.filter(isActiveEntry)
          .map((entry) => <EditorialEntry key={entry.sys.id} entry={entry} />)}
    </>
  );
}
